import { ArrowPathIcon, MagnifyingGlassIcon, CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import PriceFormat from "../../utils/priceFormat";
import useValuationScrape from "../../data/useValuationScrape";
import { IValuationResult, IValuationScrape } from "../../models/ValuationScrape";

const SITE_LABELS: Record<string, string> = {
  "cartakeback": "Car Take Back",
  "scrapcarcomparison": "Scrap Car Comparison",
  "motorwise": "Motorwise",
  "removemycar": "Remove My Car",
  "redcorn": "Redcorn",
  "webuyanycar": "We Buy Any Car",
};

function siteLabel(site: string): string {
  return SITE_LABELS[site] ?? site;
}

function ResultRow({ result }: { result: IValuationResult }) {
  const { t } = useTranslation("vehicles");
  const hasPrice = result.status === "found_price" && result.amount != null;
  const noPrice = result.status === "no_price";
  const failed = !hasPrice && !noPrice;

  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2">
        {hasPrice ? (
          <CheckCircleIcon className="h-4 w-4 text-green-500 shrink-0" />
        ) : (
          <ExclamationCircleIcon className="h-4 w-4 text-gray-400 shrink-0" />
        )}
        <span className="text-sm text-gray-700">{siteLabel(result.site)}</span>
        {result.finalUrl && (
          <a
            href={result.finalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-blue-500 hover:underline"
          >
            {t("view")}
          </a>
        )}
      </div>
      <div className="text-right">
        {hasPrice ? (
          <span className="text-sm font-semibold text-green-700">
            {PriceFormat(result.amount)}
          </span>
        ) : (
          <span className="text-xs text-gray-400">
            {noPrice ? t("No offer") : failed ? t("Error") : "—"}
          </span>
        )}
        {result.notes && (
          <p className="text-[10px] text-gray-400 mt-0.5 max-w-[160px] text-right truncate" title={result.notes}>
            {result.notes}
          </p>
        )}
      </div>
    </div>
  );
}

function ScrapeResults({ scrape }: { scrape: IValuationScrape }) {
  const { t } = useTranslation("vehicles");
  const withPrices = scrape.results.filter(r => r.status === "found_price" && r.amount != null);
  const highest = withPrices.length > 0 ? Math.max(...withPrices.map(r => r.amount!)) : null;
  const average =
    withPrices.length > 0
      ? Math.round(withPrices.reduce((sum, r) => sum + r.amount!, 0) / withPrices.length)
      : null;

  return (
    <div className="space-y-3">
      {withPrices.length > 0 && (
        <div className="flex gap-3">
          <div className="flex-1 rounded-md bg-green-50 border border-green-200 p-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-green-600">{t("Highest")}</p>
            <p className="text-base font-bold text-green-700">{PriceFormat(highest)}</p>
          </div>
          <div className="flex-1 rounded-md bg-blue-50 border border-blue-200 p-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-600">{t("Average")}</p>
            <p className="text-base font-bold text-blue-700">{PriceFormat(average)}</p>
          </div>
        </div>
      )}

      <div>
        {scrape.results.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-2">{t("No results returned.")}</p>
        ) : (
          scrape.results.map(r => <ResultRow key={r.id} result={r} />)
        )}
      </div>

      {scrape.error && (
        <p className="text-xs text-red-500 mt-1">{scrape.error}</p>
      )}

      <p className="text-[10px] text-gray-400">
        {t("Scraped {{date}}", { date: scrape.completedOn ? new Date(scrape.completedOn).toLocaleString() : "" })}
        {" · "}{scrape.reg} · {scrape.condition}
        {scrape.mileage != null ? ` · ${t("{{mileage}} mi", { mileage: scrape.mileage.toLocaleString() })}` : ""}
      </p>
    </div>
  );
}

function PendingIndicator({ status }: { status: string }) {
  const { t } = useTranslation("vehicles");
  return (
    <div className="flex flex-col items-center gap-2 py-4">
      <ArrowPathIcon className="h-6 w-6 text-indigo-500 animate-spin" />
      <p className="text-sm text-gray-600">
        {status === "Queued" ? t("Queued — waiting for scraper…") : t("Scraping prices across UK sites…")}
      </p>
    </div>
  );
}

export default function ValuationScrapeCard({ vehicleId }: { vehicleId: string }) {
  const { t } = useTranslation("vehicles");
  const { scrapes, latestScrape, isPending, initiate } = useValuationScrape(vehicleId, "ValuationScrapeCard");

  const isLoading = scrapes.isLoading;
  const hasCompleted = !!latestScrape && latestScrape.status === "Completed";
  const hasFailed = !!latestScrape && latestScrape.status === "Failed";
  const canInitiate = !isPending && !isLoading && !initiate.isLoading;

  return (
    <div className="overflow-hidden rounded-lg border border-indigo-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-indigo-100 bg-indigo-50 px-4 py-3">
        <MagnifyingGlassIcon className="h-4 w-4 text-indigo-600" />
        <h3 className="text-sm font-semibold text-indigo-800">{t("Price Comparison")}</h3>
      </div>

      <div className="p-4 space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <ArrowPathIcon className="h-5 w-5 text-gray-400 animate-spin" />
          </div>
        ) : isPending && latestScrape ? (
          <PendingIndicator status={latestScrape.status} />
        ) : hasCompleted && latestScrape ? (
          <>
            <ScrapeResults scrape={latestScrape} />
            <button
              type="button"
              disabled={!canInitiate}
              onClick={() => initiate.mutate()}
              className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("Re-run price check")}
            </button>
          </>
        ) : hasFailed && latestScrape ? (
          <>
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3">
              <ExclamationCircleIcon className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-700">{t("Scrape failed")}</p>
                {latestScrape.error && (
                  <p className="text-xs text-red-600 mt-0.5">{latestScrape.error}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              disabled={!canInitiate}
              onClick={() => initiate.mutate()}
              className="w-full rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("Retry price check")}
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500">
              {t("No price comparison data yet. Query live prices from UK car buying sites.")}
            </p>
            <button
              type="button"
              disabled={!canInitiate}
              onClick={() => initiate.mutate()}
              className="w-full rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {initiate.isLoading ? t("Queuing…") : t("Get price comparison")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
